import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Typography } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import {
  applyRegistryToGroupedNodeFields,
  buildRegistryPayload,
  probeImageAndValidateArchitecture,
  readFormImageSourceUrl,
  readRegistryAuthFromValues,
  resolveRegistryHost,
  setFormFieldError,
} from '@/delivery-type-plugin/capabilities/image';
import type { MaasGroupSpec } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  formJsonFieldFormatter,
  formJsonFieldNormalize,
  parseFormJsonField,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  clearContainerGpuResourceConfigurationsCache,
  collectBaselineNodesFromInitValues,
  findGpuUsageViolationLatest,
  formatGpuUsageValidationError,
  getCachedContainerGpuResourceConfigurations,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/gpuResourceValidation';
import { validateGatewayNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/groups-config-section/validators';
import {
  GATEWAY_NODES_FIELD,
  GATEWAY_SYSTEM_DISK_FIELD,
  GATEWAY_SYSTEM_DISK_PRICING_FIELD,
  INFERENCE_SYSTEM_FIELD,
  MASTER_NODES_FIELD,
  WORKER_NODES_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { parseNodesField, writeNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { validateNodeGroupResource } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';
import type { ContainerDeployInstanceScheduling } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD,
} from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import type { ContainerDeployStartupCommands } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import { useScheduleResourceFormPriceRefresh } from '@/delivery-type-plugin/platform/resource-form';
import { SKU_RESOURCE_MODULES_FIELD } from '@/delivery-type-plugin/platform/scene-layout/types';

import { GroupSpecCard } from './group-spec-card';
import styles from './group-spec-card/group-spec-card.module.less';
import { GroupSpecDrawer } from './group-spec-modal';
import type { MaasNodeGroupsBlockProps } from './types';

const FormItem = Form.Item;
const VALIDATE_ON_SUBMIT: string[] = [];

function resolveNodeSystemDiskGi(node: EmbeddedNodeSpec | undefined): number | undefined {
  const num = Number(node?._systemDiskSize);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

/** 模型实例网关节点：单组、无部署模式，抽屉与推理节点一致 */
export function MaasGatewayNodesBlock({ form, options }: MaasNodeGroupsBlockProps) {
  const {
    namespace,
    isEdit,
    initValues,
    billModelId,
    deploymentTypeId,
    paymentTiming,
    skuType,
    resourceEditMode,
    lockActionsWhenFilled,
    initCommandScriptHint,
    initCommandSectionDesc,
    startupCommandSectionDesc,
    initCommandEditor = 'script',
    showNodeStartupCommand = false,
    embeddedNodeConfig,
    showInferenceFramework,
    showTrainingImage,
    showService,
    showHealthCheck,
    showAdvancedConfig,
    showNic,
    customImageOnly,
    presetArchitecture,
    presetImageLocked,
    nicPricingOptions,
    requireGpuSoftware,
  } = options || {};
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const fullyReadOnly = sectionReadOnly || isOrderFixed;
  const schedulePriceRefresh = useScheduleResourceFormPriceRefresh();
  const gatewayRoleLabel = t('cluster:gateway');

  const gatewayNodesRaw = Form.useWatch(GATEWAY_NODES_FIELD, form);
  const masterNodesRaw = Form.useWatch(MASTER_NODES_FIELD, form);
  const workerNodesRaw = Form.useWatch(WORKER_NODES_FIELD, form);
  const [gatewayNode, setGatewayNode] = useState<MaasGroupSpec | undefined>(() => parseNodesField(initValues?.gatewayNodes)[0]);
  const [nodeModalVisible, setNodeModalVisible] = useState(false);
  const [openingNodeModal, setOpeningNodeModal] = useState(false);
  const skipExternalSyncRef = useRef(true);

  useEffect(() => {
    void getCachedContainerGpuResourceConfigurations();
    return () => clearContainerGpuResourceConfigurationsCache();
  }, []);

  useEffect(() => {
    const fromInit = parseNodesField(initValues?.gatewayNodes)[0];
    if (fromInit) {
      setGatewayNode(fromInit);
      writeNodesField(form, GATEWAY_NODES_FIELD, [fromInit]);
      return;
    }
    const fromForm = parseNodesField(form.getFieldValue(GATEWAY_NODES_FIELD))[0];
    if (fromForm) setGatewayNode(fromForm);
  }, [form, initValues?.gatewayNodes]);

  useEffect(() => {
    if (skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false;
      return;
    }
    setGatewayNode(parseNodesField(gatewayNodesRaw)[0]);
  }, [gatewayNodesRaw]);

  const inferenceSystemRaw = Form.useWatch(INFERENCE_SYSTEM_FIELD, form);
  const inferenceSystem = useMemo(() => parseFormJsonField<Record<string, unknown>>(inferenceSystemRaw), [inferenceSystemRaw]);
  const imageContainerDeployInstanceSchedulingRaw = Form.useWatch(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, form);
  const imageContainerDeployInstanceScheduling = useMemo(
    () => parseFormJsonField<ContainerDeployInstanceScheduling>(imageContainerDeployInstanceSchedulingRaw),
    [imageContainerDeployInstanceSchedulingRaw],
  );
  const imageContainerDeployStartupRaw = Form.useWatch(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD, form);
  const imageContainerDeployStartup = useMemo(
    () => parseFormJsonField<ContainerDeployStartupCommands>(imageContainerDeployStartupRaw),
    [imageContainerDeployStartupRaw],
  );

  const presetHasGateway = useMemo(() => {
    if (!initValues) return false;
    if (parseNodesField(initValues.gatewayNodes).length > 0) return true;
    return !!(initValues.gateway && typeof initValues.gateway === 'object');
  }, [initValues]);
  const nodeActionsLocked = Boolean(lockActionsWhenFilled) && presetHasGateway && !fullyReadOnly;
  const nodeCardReadonly = fullyReadOnly || nodeActionsLocked;
  const gpuValidationOptions = useMemo(
    () => (isEdit ? { baselineNodes: collectBaselineNodesFromInitValues(initValues) } : undefined),
    [initValues, isEdit],
  );

  const shouldProbeBeforeOpen = !showInferenceFramework && !showTrainingImage && !customImageOnly;
  const openModal = useCallback(() => setNodeModalVisible(true), []);
  const closeNodeModal = useCallback(() => setNodeModalVisible(false), []);

  const probeThenOpenNodeModal = useCallback(async () => {
    if (openingNodeModal) return;
    if (!shouldProbeBeforeOpen) {
      openModal();
      return;
    }
    setOpeningNodeModal(true);
    try {
      const values = form.getFieldsValue() as Record<string, unknown>;
      const sourceUrl = readFormImageSourceUrl(
        form,
        [`${INFERENCE_SYSTEM_FIELD}.image`, `${INFERENCE_SYSTEM_FIELD}.url`],
        INFERENCE_SYSTEM_FIELD,
      );
      const registryAuth = readRegistryAuthFromValues(values);
      const probed = await probeImageAndValidateArchitecture({
        sourceUrl,
        registryAuth,
        requireArchitecture: false,
        sourceUrlField: `${INFERENCE_SYSTEM_FIELD}.image`,
        emptySourceMessage: t('data-center:please.enter.image'),
        t,
      });
      if (probed.ok === false) {
        setFormFieldError(form, probed.field || `${INFERENCE_SYSTEM_FIELD}.image`, probed.message);
        return;
      }
      const registry = buildRegistryPayload(resolveRegistryHost(probed.registryHost, sourceUrl), registryAuth);
      applyRegistryToGroupedNodeFields(form, registry);
      openModal();
    } finally {
      setOpeningNodeModal(false);
    }
  }, [form, openModal, openingNodeModal, shouldProbeBeforeOpen, t]);

  const handleNodeModalOk = useCallback(
    (values: MaasGroupSpec) => {
      const next = cloneDeep(values);
      setGatewayNode(next);
      writeNodesField(form, GATEWAY_NODES_FIELD, [next]);
      setNodeModalVisible(false);
      schedulePriceRefresh(form);
    },
    [form, schedulePriceRefresh],
  );

  const specForCard = gatewayNode && validateNodeGroupResource(gatewayNode) ? gatewayNode : undefined;
  const modalReplicasMin = isEdit && gatewayNode?.replicas === 0 ? 0 : 1;
  const peerNodes = useMemo(
    () => [...parseNodesField(masterNodesRaw), ...parseNodesField(workerNodesRaw)],
    [masterNodesRaw, workerNodesRaw],
  );

  return (
    <>
      <div className={styles.groupsPanel}>
        <FormItem
          field={GATEWAY_NODES_FIELD}
          hidden
          validateTrigger={VALIDATE_ON_SUBMIT}
          rules={[
            {
              async validator(value, callback) {
                try {
                  const modules = form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as { gatewayConfig?: boolean } | undefined;
                  if (modules?.gatewayConfig !== true) {
                    callback();
                    return;
                  }
                  const baseError = validateGatewayNodesField(value, t);
                  if (baseError) {
                    callback(baseError);
                    return;
                  }
                  const node = parseNodesField(value)[0] as EmbeddedNodeSpec | undefined;
                  const violation = await findGpuUsageViolationLatest([...peerNodes, ...(node ? [node] : [])], gpuValidationOptions);
                  callback(violation ? formatGpuUsageValidationError(t, violation) : undefined);
                } catch (error) {
                  console.log(error);
                  callback();
                }
              },
            },
          ]}
          style={{ display: 'none', height: 0, margin: 0 }}
        >
          <span />
        </FormItem>
        <div className={styles.cardList}>
          <GroupSpecCard
            role="master"
            masterRoleLabel={gatewayRoleLabel}
            spec={specForCard}
            systemDiskGi={embeddedNodeConfig ? resolveNodeSystemDiskGi(specForCard as EmbeddedNodeSpec) : undefined}
            onEdit={() => void probeThenOpenNodeModal()}
            readonly={specForCard ? nodeCardReadonly : fullyReadOnly || openingNodeModal}
            showNodeStartupCommand={showNodeStartupCommand}
          />
        </div>
        <FormItem shouldUpdate noStyle>
          {(_, formInstance: FormInstance) => {
            const error = formInstance.getFieldError(GATEWAY_NODES_FIELD);
            if (!error?.message) return null;
            return (
              <Typography.Text type="error" className={styles.fieldError}>
                {error.message}
              </Typography.Text>
            );
          }}
        </FormItem>
      </div>

      <FormItem
        field={IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD}
        hidden
        style={{ display: 'none', margin: 0 }}
        triggerPropName="value"
        formatter={formJsonFieldFormatter}
        normalize={formJsonFieldNormalize}
      >
        <Input />
      </FormItem>
      <FormItem
        field={IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD}
        hidden
        style={{ display: 'none', margin: 0 }}
        triggerPropName="value"
        formatter={formJsonFieldFormatter}
        normalize={formJsonFieldNormalize}
      >
        <Input />
      </FormItem>

      <GroupSpecDrawer
        visible={nodeModalVisible}
        role="master"
        initialValues={gatewayNode}
        hideReplicas={false}
        replicasMin={modalReplicasMin}
        namespace={namespace}
        isEdit={isEdit}
        baselineNodes={gpuValidationOptions?.baselineNodes}
        onOk={handleNodeModalOk}
        onCancel={closeNodeModal}
        parentForm={form}
        systemDiskSizeField={GATEWAY_SYSTEM_DISK_FIELD}
        systemDiskPricingField={GATEWAY_SYSTEM_DISK_PRICING_FIELD}
        systemDiskRoleLabel={gatewayRoleLabel}
        presetConfigSnapshot={initValues as Record<string, unknown> | undefined}
        inferenceSystem={inferenceSystem}
        instanceScheduling={imageContainerDeployInstanceScheduling}
        peerNodes={peerNodes}
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        paymentTiming={paymentTiming}
        skuType={skuType}
        resourceEditMode={resourceEditMode}
        lockActionsWhenFilled={lockActionsWhenFilled}
        initCommandScriptHint={initCommandScriptHint}
        initCommandSectionDesc={initCommandSectionDesc}
        startupCommandSectionDesc={startupCommandSectionDesc}
        initCommandEditor={initCommandEditor}
        showNodeStartupCommand={showNodeStartupCommand}
        imageContainerDeployStartup={imageContainerDeployStartup}
        defaultName="gateway"
        masterGroupTitle={t('data-center:maas.gateway.node.config')}
        embeddedNodeConfig={embeddedNodeConfig}
        showInferenceFramework={showInferenceFramework}
        showTrainingImage={showTrainingImage}
        showService={showService}
        showHealthCheck={showHealthCheck}
        showAdvancedConfig={showAdvancedConfig ?? showHealthCheck}
        showNic={showNic}
        customImageOnly={customImageOnly}
        presetArchitecture={presetArchitecture}
        presetImageLocked={presetImageLocked}
        nicPricingOptions={nicPricingOptions}
        requireGpuSoftware={requireGpuSoftware}
      />
    </>
  );
}
